import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CreateTaskNoteRequest } from '@vagusx/clients';
import { getTaskOverviewClient } from '@/lib/api-clients';
import { queryKeys } from '@/lib/query-keys';
import { CACHE_TIME_10_MINS } from '@/lib/query-client';

/**
 * Hooks for the task overview tab — timeline, related tasks, internal
 * notes, and the LLM-generated summary. Each is keyed by `taskId` and
 * disabled when no id is supplied so callers can mount them
 * unconditionally and let `enabled` guard the request.
 */

export function useTaskTimeline(taskId: string | undefined) {
  return useQuery({
    queryKey: taskId ? queryKeys.tasks.timeline(taskId) : ['tasks', 'timeline', 'noop'],
    queryFn: () => getTaskOverviewClient().getTimeline(taskId!),
    enabled: !!taskId,
    staleTime: CACHE_TIME_10_MINS,
  });
}

export function useTaskRelated(taskId: string | undefined) {
  return useQuery({
    queryKey: taskId ? queryKeys.tasks.related(taskId) : ['tasks', 'related', 'noop'],
    queryFn: () => getTaskOverviewClient().getRelated(taskId!),
    enabled: !!taskId,
    staleTime: CACHE_TIME_10_MINS,
  });
}

export function useTaskNotes(taskId: string | undefined) {
  return useQuery({
    queryKey: taskId ? queryKeys.tasks.notes(taskId) : ['tasks', 'notes', 'noop'],
    queryFn: () => getTaskOverviewClient().listNotes(taskId!),
    enabled: !!taskId,
    staleTime: CACHE_TIME_10_MINS,
  });
}

/**
 * Summary endpoint is a POST (it may write to the cache), so we expose
 * it as a mutation rather than a query. Callers fire it on tab open;
 * `regenerate` opts into a fresh LLM call. The result is also pushed
 * into the query cache so subsequent reads (e.g. unrelated re-renders)
 * see the cached blob without re-firing.
 */
export function useTaskSummary(taskId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ regenerate = false }: { regenerate?: boolean } = {}) =>
      getTaskOverviewClient().getSummary(taskId!, regenerate),
    onSuccess: (data) => {
      if (taskId) {
        queryClient.setQueryData(queryKeys.tasks.summary(taskId), data);
      }
    },
  });
}

export function useCreateTaskNote(taskId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateTaskNoteRequest) => getTaskOverviewClient().createNote(taskId!, data),
    onSuccess: () => {
      if (!taskId) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.notes(taskId) });
      // Note shows up in the timeline too — bust that cache.
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.timeline(taskId) });
    },
  });
}

export function useDeleteTaskNote(taskId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (noteId: string) => getTaskOverviewClient().deleteNote(taskId!, noteId),
    onSuccess: () => {
      if (!taskId) return;
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.notes(taskId) });
      queryClient.invalidateQueries({ queryKey: queryKeys.tasks.timeline(taskId) });
    },
  });
}
